import React, { FunctionComponent, useMemo } from 'react'
import EMongoliaOtp from '@/components/page/emongoliaOtp'
import useIntl from '@/utils/intl'
import { useAuth } from '@/context/AuthContext'
import { resolveImageUrl } from '@/utils/constants'
import Image from 'next/image'
import DarkButton from '@/components/ui/Buttons/SecondaryButton'

interface Props {
  instanceId: string
  errorType?: 'BLACKLIST' | 'UNDERAGE' | 'CORP_ERROR'
  errorMessage?: string
}

type ErrorType = 'BLACKLIST' | 'UNDERAGE' | 'CORP_ERROR'

const isValidErrorType = (value: string): value is ErrorType => {
  return ['BLACKLIST', 'UNDERAGE', 'CORP_ERROR'].includes(value)
}

const EMongolia: FunctionComponent<Props> = ({ instanceId, errorType, errorMessage }) => {
  const { translate } = useIntl()
  const { isWebView } = useAuth()

  const derivedErrorType: ErrorType | undefined = useMemo(() => {
    const normalizedMessage = (errorMessage ?? '').toUpperCase().trim()
    if (isValidErrorType(normalizedMessage)) {
      return normalizedMessage
    }
    return errorType
  }, [errorMessage, errorType])

  const hasErrorType = derivedErrorType !== undefined
  const inWebView = Boolean(isWebView)

  const content = useMemo(() => {
    if (hasErrorType && derivedErrorType !== undefined) {
      const errorContent = {
        BLACKLIST: {
          image: resolveImageUrl('/uploads/icons/handset_fail_web.svg'),
          title: translate('leasing.blacklist.title'),
          description: translate('leasing.blacklist.description')
        },
        UNDERAGE: {
          image: resolveImageUrl('/uploads/icons/handset_fail_web.svg'),
          title: translate('leasing.underage.title'),
          description: translate('leasing.underage.description')
        },
        CORP_ERROR: {
          image: resolveImageUrl('/uploads/icons/handset_fail_web.svg'),
          title: translate('leasing.corpError.title'),
          description: translate('leasing.corpError.description')
        }
      }
      return errorContent[derivedErrorType]
    }

    return {
      image: resolveImageUrl('/uploads/icons/mobot.svg'),
      title: translate('leasing.eMongoliaText'),
      description: translate('leasing.eMongoliaText')
    }
  }, [hasErrorType, derivedErrorType, translate])

  const showOtpComponent = !hasErrorType

  const exitWebview = (): void => {
    let webView
    if ((window as any).mobiBridge !== undefined) {
      webView = (window as any).mobiBridge
    }
    if ((window as any).ReactNativeWebview !== undefined) {
      webView = (window as any).ReactNativeWebview
    }
    if (webView !== undefined) {
      const data = { key: 'exit' }
      webView.postMessage(JSON.stringify(data))
    } else {
      console.warn('Webview bridge not found')
    }
  }

  if (hasErrorType) {
    return (
      <div
        className={`bg-surfaceInverse rounded-xl md:shadow w-full h-full ${
          inWebView ? 'md:h-[40vh]' : 'md:h-[55vh]'
        } md:w-fit flex flex-col ${
          isWebView ? 'min-h-screen justify-center' : 'justify-start'
        } relative no-scrollbar`}
      >
        <div className='flex-grow px-4 py-4 sm:px-8 sm:py-8 overflow-auto no-scrollbar flex flex-col gap-4 mt-40 sm:mt-0'>
          <div className='flex items-center justify-center'>
            <Image
              src={content.image}
              width={64}
              height={64}
              alt={derivedErrorType?.toLowerCase() ?? 'error'}
            />
          </div>
          <span className='text-textHigh font-extrabold text-lg text-center'>
            {content.title}
          </span>
          <span className='text-textMid text-sm text-center whitespace-pre-line line-clamp-4'>
            {content.description}
          </span>
        </div>

        <div className='fixed md:sticky bottom-0 rounded-b-xl right-0 left-0 z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
          <div className='md:w-96 w-full flex'>
            <DarkButton
              fullwidth
              onClick={exitWebview}
            >
              {translate('continue')}
            </DarkButton>
          </div>
        </div>
      </div>
    )
  }

  return (
    showOtpComponent
      ? (
        <EMongoliaOtp instanceId={instanceId} service='handset' />
        )
      : null
  )
}

export default EMongolia
